import { useEffect, useRef, useState } from 'react'
import { Popover, type PopoverItem } from './Popover'
import { ConfirmDialog } from './ConfirmDialog'
import { PromptDialog } from './PromptDialog'
import { MoveDialog, MOVE_TARGET_ROOT, type MoveItem } from './MoveDialog'
import { useToast } from './Toast'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  useFolderTree, useDocumentsByProject,
  useCreateFolder, useUpdateFolder, useDeleteFolder, useMoveFolder, useReorderFolders,
  useDeleteDocument, useUpdateDocument, useMoveDocument,
} from '../lib/queries'
import { apiPost, apiPut, apiDelete } from '../lib/api'
import { triggerDownload } from '../lib/download'
import { ICON_PENCIL, ICON_TRASH, ICON_PLUS, ICON_FILE, ICON_OPEN_EXTERNAL, ICON_LOCK, ICON_LINK, ICON_MOVE, ICON_DOWNLOAD } from '../lib/icons'
import type { FolderDto, DocumentDto, ProjectDto } from '../lib/types'

interface SidebarProps {
  project: ProjectDto
  selectedFolderUuid: string | null
  selectedDocUuid?: string
  onSelectFolder: (uuid: string | null) => void
  onSelectDoc?: (doc: DocumentDto) => void
  onCreateDocument?: (folderUuid: string) => void
}

interface PopoverState { anchor: { x: number; y: number }; items: PopoverItem[] }
interface DragInfo { kind: 'folder' | 'doc'; uuid: string; parentFolderUuid: string }
interface DropTarget { kind: 'folder' | 'doc' | 'root'; uuid: string; position: 'before' | 'after' | 'inside' }


const FOLDER_MIME = 'application/x-folder-uuid'
const DOC_MIME = 'application/x-doc-uuid'

export function Sidebar(props: SidebarProps) {
  const { project, selectedFolderUuid, selectedDocUuid, onSelectFolder, onSelectDoc, onCreateDocument } = props
  const navigate = useNavigate()
  const qc = useQueryClient()
  const tree = useFolderTree(project.uuid)
  const docs = useDocumentsByProject(project.uuid)
  const createFolder = useCreateFolder(project.uuid)
  const updateFolder = useUpdateFolder(project.uuid)
  const deleteFolder = useDeleteFolder(project.uuid)
  const moveFolder = useMoveFolder(project.uuid)
  const reorderFolders = useReorderFolders(project.uuid)
  const updateDocument = useUpdateDocument(project.uuid)
  const deleteDocument = useDeleteDocument(project.uuid)
  const moveDocument = useMoveDocument(project.uuid)
  const toast = useToast()

  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(project.uuid))
  const [popover, setPopover] = useState<PopoverState | null>(null)
  const [renameFolder, setRenameFolder] = useState<FolderDto | null>(null)
  const [renameDoc, setRenameDoc] = useState<DocumentDto | null>(null)
  const [renameProjectOpen, setRenameProjectOpen] = useState(false)
  const [newChildOf, setNewChildOf] = useState<{ parent: FolderDto | null; isRoot: boolean } | null>(null)
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<FolderDto | null>(null)
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<DocumentDto | null>(null)
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false)
  /** Move dialog 가 열릴 때 옮길 대상 — 단건 (⋯ menu) 또는 multi (toolbar) 둘 다 같은 흐름 */
  const [moveItems, setMoveItems] = useState<MoveItem[] | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)
  /** Multi-select — Cmd/Ctrl+click 토글, Shift+click range. 1+ 선택 시 toolbar 노출. key = `${kind}:${uuid}` */
  const [selected, setSelected] = useState<Map<string, MoveItem>>(new Map())
  /** Shift+click 의 기준점 — 마지막으로 cmd/ctrl/shift+click 한 row */
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null)
  const toggleSelect = (item: MoveItem) => {
    const key = `${item.kind}:${item.uuid}`
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(key)) next.delete(key); else next.set(key, item)
      return next
    })
  }
  const isSelected = (kind: 'document' | 'folder', uuid: string) => selected.has(`${kind}:${uuid}`)
  const clearSelection = () => { setSelected(new Map()); setSelectionAnchor(null) }
  // dragInfo 는 ref — 같은 event tick (test dispatchEvent) 에서 setState 반영 못 받는 문제 회피
  const dragInfoRef = useRef<DragInfo | null>(null)
  const dropTargetRef = useRef<DropTarget | null>(null)
  const [dropTarget, setDropTargetState] = useState<DropTarget | null>(null)
  const setDropTarget = (next: DropTarget | null | ((prev: DropTarget | null) => DropTarget | null)) => {
    const value = typeof next === 'function' ? next(dropTargetRef.current) : next
    dropTargetRef.current = value
    setDropTargetState(value)
  }

  useEffect(() => { saveCollapsed(project.uuid, collapsed) }, [project.uuid, collapsed])
  /** project 전환 시 selection/moveDialog 초기화 — 다른 프로젝트의 uuid 들이 섞이는 것 방지 */
  useEffect(() => { clearSelection(); setMoveItems(null); setMoveError(null) }, [project.uuid])

  const toggleCollapse = (uuid: string) => setCollapsed((prev) => {
    const next = new Set(prev)
    if (next.has(uuid)) next.delete(uuid); else next.add(uuid)
    return next
  })

  /** 모든 폴더 uuid (depth 무관) — Collapse all 용 */
  const collectAllFolderUuids = (folders: FolderDto[] | undefined): string[] => {
    const out: string[] = []
    const walk = (arr: FolderDto[]) => {
      for (const f of arr) { out.push(f.uuid); if (f.children) walk(f.children) }
    }
    walk(folders ?? [])
    return out
  }
  const expandAll = () => setCollapsed(new Set())
  const collapseAll = () => setCollapsed(new Set(collectAllFolderUuids(tree.data)))

  /** Scroll-from-Source: 현재 열린 doc 의 부모 폴더 chain 모두 expand + 그 li scrollIntoView. */
  const scrollFromSource = () => {
    if (!selectedDocUuid) return
    const doc = (docs.data ?? []).find((d) => d.uuid === selectedDocUuid)
    if (!doc) return
    // doc.folderId → folder chain (chain uuid 들 collapsed Set 에서 제거)
    const chainUuids: string[] = []
    let curId: number | null | undefined = doc.folderId
    let safety = 0
    while (curId != null && safety++ < 64) {
      const f = folderById.get(curId)
      if (!f) break
      chainUuids.push(f.uuid)
      curId = f.parentId ?? null
    }
    setCollapsed((prev) => {
      const next = new Set(prev)
      for (const u of chainUuids) next.delete(u)
      return next
    })
    setTimeout(() => {
      document.querySelector(`li[data-doc-uuid="${selectedDocUuid}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 80)
  }

  /** 문서 영구 link (`origin/d/{uuid}`) 클립보드 복사 + 토스트 */
  const copyDocLink = (d: DocumentDto) => {
    const url = `${window.location.origin}/d/${d.uuid}`
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => toast.show('Link copied!'),
        () => toast.show('Copy failed'),
      )
    } else {
      // 비보안 컨텍스트 fallback — execCommand
      const ta = document.createElement('textarea')
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0'
      document.body.appendChild(ta); ta.select()
      try { document.execCommand('copy'); toast.show('Link copied!') }
      catch { toast.show('Copy failed') }
      finally { document.body.removeChild(ta) }
    }
  }

  // ── 데이터 정리 ──
  const folderById = new Map<number, FolderDto>()
  const folderByUuid = new Map<string, FolderDto>()
  const folderParents = new Map<string, string>() // uuid -> parentUuid('' for root)
  const walkFolders = (arr: FolderDto[], parentUuid: string) => {
    for (const f of arr) {
      folderById.set(f.id, f); folderByUuid.set(f.uuid, f); folderParents.set(f.uuid, parentUuid)
      if (f.children) walkFolders(f.children, f.uuid)
    }
  }
  walkFolders(tree.data ?? [], '')

  const docsByFolder = new Map<number, DocumentDto[]>()
  const rootDocs: DocumentDto[] = []
  for (const d of docs.data ?? []) {
    if (d.archived) continue
    if (d.folderId == null) rootDocs.push(d)
    else {
      const arr = docsByFolder.get(d.folderId) ?? []
      arr.push(d)
      docsByFolder.set(d.folderId, arr)
    }
  }
  // 사이드바 doc 목록은 파일명 오름차순 (사용자 의도 — drag reorder 제거)
  const titleSort = (a: DocumentDto, b: DocumentDto) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' })
  for (const arr of docsByFolder.values()) arr.sort(titleSort)
  rootDocs.sort(titleSort)

  /** 가시 트리 평탄화 — collapsed 안 항목은 제외. shift+click range 계산용. */
  const buildFlatVisible = (): MoveItem[] => {
    const out: MoveItem[] = []
    const walk = (folders: FolderDto[]) => {
      for (const f of folders) {
        out.push({ kind: 'folder', uuid: f.uuid, name: f.name })
        if (!collapsed.has(f.uuid)) {
          if (f.children?.length) walk(f.children)
          for (const d of docsByFolder.get(f.id) ?? []) {
            out.push({ kind: 'document', uuid: d.uuid, name: d.title })
          }
        }
      }
    }
    if (tree.data) walk(tree.data)
    for (const d of rootDocs) out.push({ kind: 'document', uuid: d.uuid, name: d.title })
    return out
  }

  /**
   * 행 click 시 modifier 검사. handled=true 면 호출자는 default(open) 동작 skip.
   * - Cmd/Ctrl+click: 단일 토글 + anchor 갱신
   * - Shift+click: anchor 부터 현재 row 까지 가시 범위 모두 selection 에 추가 (anchor 없으면 단일 토글로 fallback)
   */
  const handleSelectionClick = (e: React.MouseEvent, item: MoveItem): boolean => {
    const isCmd = e.metaKey || e.ctrlKey
    const isShift = e.shiftKey
    if (!isCmd && !isShift) return false
    e.preventDefault(); e.stopPropagation()
    const key = `${item.kind}:${item.uuid}`
    if (isShift && selectionAnchor) {
      const flat = buildFlatVisible()
      const idxA = flat.findIndex((it) => `${it.kind}:${it.uuid}` === selectionAnchor)
      const idxB = flat.findIndex((it) => `${it.kind}:${it.uuid}` === key)
      if (idxA >= 0 && idxB >= 0) {
        const lo = Math.min(idxA, idxB), hi = Math.max(idxA, idxB)
        setSelected((prev) => {
          const next = new Map(prev)
          for (let i = lo; i <= hi; i++) {
            const it = flat[i]
            next.set(`${it.kind}:${it.uuid}`, it)
          }
          return next
        })
        return true
      }
    }
    toggleSelect(item)
    setSelectionAnchor(key)
    return true
  }

  // ── 메뉴 ──
  const folderMenu = (f: FolderDto): PopoverItem[] => [
    { label: 'New document', icon: ICON_PLUS, onClick: () => onCreateDocument?.(f.uuid) },
    { label: 'New child folder', icon: ICON_PLUS, onClick: () => setNewChildOf({ parent: f, isRoot: false }) },
    { label: 'Rename', icon: ICON_PENCIL, onClick: () => setRenameFolder(f) },
    { label: 'Move…', icon: ICON_MOVE, onClick: () => { setMoveError(null); setMoveItems([{ kind: 'folder', uuid: f.uuid, name: f.name }]) } },
    { label: 'Download as ZIP (MD)', icon: ICON_DOWNLOAD, onClick: () => triggerDownload(`/api/folders/${f.uuid}/export?format=md`) },
    { label: 'Download as ZIP (PDF)', icon: ICON_DOWNLOAD, onClick: () => triggerDownload(`/api/folders/${f.uuid}/export?format=pdf`) },
    { label: 'Delete', icon: ICON_TRASH, danger: true, onClick: () => setConfirmDeleteFolder(f) },
  ]
  const docMenu = (d: DocumentDto): PopoverItem[] => [
    { label: 'Open', icon: ICON_OPEN_EXTERNAL, onClick: () => onSelectDoc?.(d) },
    { label: 'Edit', icon: ICON_PENCIL, onClick: () => navigate(`/d/${d.uuid}?edit=1`) },
    { label: 'Versions', icon: ICON_FILE, onClick: () => navigate(`/d/${d.uuid}/versions`) },
    { label: 'Copy link', icon: ICON_LINK, onClick: () => copyDocLink(d) },
    { label: 'Rename', icon: ICON_FILE, onClick: () => setRenameDoc(d) },
    { label: 'Move…', icon: ICON_MOVE, onClick: () => { setMoveError(null); setMoveItems([{ kind: 'document', uuid: d.uuid, name: d.title }]) } },
    { label: 'Download as MD', icon: ICON_DOWNLOAD, onClick: () => triggerDownload(`/api/documents/${d.uuid}/export?format=md`) },
    { label: 'Download as PDF', icon: ICON_DOWNLOAD, onClick: () => triggerDownload(`/api/documents/${d.uuid}/export?format=pdf`) },
    { label: 'Delete', icon: ICON_TRASH, danger: true, onClick: () => setConfirmDeleteDoc(d) },
  ]
  const projectMenu = (): PopoverItem[] => [
    { label: 'New folder', icon: ICON_PLUS, onClick: () => setNewChildOf({ parent: null, isRoot: true }) },
    { label: 'Rename project', icon: ICON_PENCIL, onClick: () => setRenameProjectOpen(true) },
    {
      label: project.publicReadable ? 'Disable share (Public)' : 'Enable share',
      icon: project.publicReadable ? ICON_LOCK : ICON_LINK,
      onClick: async () => {
        const action = project.publicReadable ? 'disable' : 'enable'
        try {
          await apiPost(`/api/projects/${project.uuid}/share/${action}`, {})
          qc.invalidateQueries({ queryKey: ['projects', 'key', project.projectKey] })
          toast.show(action === 'enable' ? 'Project is now public' : 'Project is now private')
        } catch {
          toast.show('Share toggle failed')
        }
      },
    },
    { label: 'Delete project', icon: ICON_TRASH, danger: true, onClick: () => setConfirmDeleteProject(true) },
  ]

  const openPopoverFor = (e: React.MouseEvent, items: PopoverItem[]) => {
    e.stopPropagation(); e.preventDefault()
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setPopover({ anchor: { x: rect.right - 200, y: rect.bottom + 4 }, items })
  }

  // ── Drag drop helpers ──
  // descendant 검사 (cross-parent move 시 자손 폴더로 이동 차단)
  const isDescendantOf = (ancestorUuid: string, candidateUuid: string): boolean => {
    let cur: string | undefined = candidateUuid
    const seen = new Set<string>()
    while (cur && !seen.has(cur)) {
      if (cur === ancestorUuid) return true
      seen.add(cur)
      cur = folderParents.get(cur)
    }
    return false
  }

  const onFolderDragStart = (e: React.DragEvent, f: FolderDto) => {
    e.dataTransfer.setData(FOLDER_MIME, f.uuid)
    e.dataTransfer.effectAllowed = 'move'
    dragInfoRef.current = ({ kind: 'folder', uuid: f.uuid, parentFolderUuid: folderParents.get(f.uuid) ?? '' })
  }
  /** doc drag — folder 안으로만 drop (다른 doc 위 reorder 는 X, 사이드바는 항상 title 정렬) */
  const onDocDragStart = (e: React.DragEvent, d: DocumentDto) => {
    e.dataTransfer.setData(DOC_MIME, d.uuid)
    e.dataTransfer.effectAllowed = 'move'
    dragInfoRef.current = ({ kind: 'doc', uuid: d.uuid, parentFolderUuid: d.folderId ? (folderById.get(d.folderId)?.uuid ?? '') : '' })
  }

  const onFolderDragOver = (e: React.DragEvent, target: FolderDto) => {
    const _di = dragInfoRef.current; if (!_di) return
    if (_di.kind === 'folder') {
      if (_di.uuid === target.uuid) return
      if (isDescendantOf(_di.uuid, target.uuid)) return
    }
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    const liEl = (e.target as HTMLElement).closest<HTMLElement>(`.folder-item[data-folder-uuid="${target.uuid}"]`)
      ?? (e.currentTarget as HTMLElement)
    const rect = liEl.getBoundingClientRect()
    const offY = e.clientY - rect.top
    let position: 'before' | 'after' | 'inside'
    if (_di.kind === 'doc') {
      position = 'inside' // doc → folder = 그 폴더 안으로 move
    } else {
      // folder → folder: 위 1/4 = before, 아래 1/4 = after, 가운데 = inside
      if (offY < rect.height * 0.25) position = 'before'
      else if (offY > rect.height * 0.75) position = 'after'
      else position = 'inside'
    }
    setDropTarget({ kind: 'folder', uuid: target.uuid, position })
  }
  const onRootDragOver = (e: React.DragEvent) => {
    const _di = dragInfoRef.current; if (!_di || _di.kind !== 'folder') return
    // li 위 drop 은 그 li 가 처리 (stopPropagation) — 빈 영역만 root drop
    if ((e.target as HTMLElement).closest('.folder-item')) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropTarget({ kind: 'root', uuid: '', position: 'inside' })
  }

  const clearDrag = () => { dragInfoRef.current = (null); setDropTarget(null) }

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    const di = dragInfoRef.current
    const dt = dropTargetRef.current
    if (!di || !dt) { clearDrag(); return }
    clearDrag()

    if (di.kind === 'folder') {
      if (dt.kind === 'root') {
        await moveFolder.mutateAsync({ uuid: di.uuid, parentUuid: null }).catch(() => {/* ignore */})
        return
      }
      if (dt.kind === 'folder') {
        const target = folderByUuid.get(dt.uuid); if (!target) return
        if (dt.position === 'inside') {
          // dragged 의 새 부모 = target. 이미 같은 부모면 no-op.
          if (di.parentFolderUuid === target.uuid) return
          await moveFolder.mutateAsync({ uuid: di.uuid, parentUuid: target.uuid }).catch(() => {/* ignore */})
        } else {
          // before/after → 같은 부모 reorder
          const targetParent = folderParents.get(target.uuid) ?? ''
          if (di.parentFolderUuid !== targetParent) {
            // 다른 부모 → 일단 move 한 후 reorder 별도. 단순히 move 만.
            await moveFolder.mutateAsync({ uuid: di.uuid, parentUuid: targetParent || null }).catch(() => {/* ignore */})
            return
          }
          // 같은 부모 — siblings 순서 계산
          const siblingArr = targetParent
            ? (folderByUuid.get(targetParent)?.children ?? [])
            : (tree.data ?? [])
          const ordered = siblingArr.map((s) => s.uuid)
          const fromIdx = ordered.indexOf(di.uuid)
          if (fromIdx >= 0) ordered.splice(fromIdx, 1)
          const targetIdx = ordered.indexOf(target.uuid)
          if (targetIdx < 0) return
          ordered.splice(dt.position === 'before' ? targetIdx : targetIdx + 1, 0, di.uuid)
          await reorderFolders.mutateAsync({ parentUuid: targetParent || null, uuids: ordered }).catch(() => {/* ignore */})
        }
      }
      return
    }

    // doc → folder 이동만 (folder 안 reorder X — 사이드바는 항상 title 정렬)
    if (di.kind === 'doc' && dt.kind === 'folder') {
      const target = folderByUuid.get(dt.uuid); if (!target) return
      if (di.parentFolderUuid !== target.uuid) {
        await moveDocument.mutateAsync({ uuid: di.uuid, folderUuid: target.uuid }).catch(() => {/* ignore */})
      }
    }
  }

  const dropClass = (kind: 'folder' | 'doc' | 'root', uuid: string): string => {
    if (!dropTarget || dropTarget.kind !== kind || dropTarget.uuid !== uuid) return ''
    if (dropTarget.position === 'inside') return ' drop-target'
    return dropTarget.position === 'before' ? ' drop-indicator-before' : ' drop-indicator-after'
  }

  const renderTree = (folders: FolderDto[], depth: number, parentUuid: string): React.ReactNode[] => {
    const out: React.ReactNode[] = []
    for (const f of folders) {
      const childCount = (f.children?.length ?? 0) + (docsByFolder.get(f.id)?.length ?? 0)
      const hasKids = childCount > 0
      const isCollapsed = collapsed.has(f.uuid)
      out.push(
        <li
          key={f.uuid}
          className={`folder-item${selectedFolderUuid === f.uuid ? ' active' : ''}${isSelected('folder', f.uuid) ? ' multi-selected' : ''}${dropClass('folder', f.uuid)}`}
          data-folder-uuid={f.uuid}
          data-parent-folder-uuid={parentUuid}
          style={{ paddingLeft: depth * 14 + 8 }}
          draggable
          onDragStart={(e) => onFolderDragStart(e, f)}
          onDragOver={(e) => onFolderDragOver(e, f)}
          onDragLeave={() => setDropTarget((dt) => dt && dt.kind === 'folder' && dt.uuid === f.uuid ? null : dt)}
          onDrop={onDrop}
          onDragEnd={clearDrag}
          onClick={(e) => {
            const t = e.target as HTMLElement
            if (t.closest('.row-actions-inline')) return
            if (t.closest('.folder-chevron')) return
            if (t.closest('.uuid-badge')) return
            if (handleSelectionClick(e, { kind: 'folder', uuid: f.uuid, name: f.name })) return
            onSelectFolder(f.uuid)
          }}
          onContextMenu={(e) => openPopoverFor(e, folderMenu(f))}
        >
          <span className={`folder-chevron${hasKids ? (isCollapsed ? '' : ' expanded') : ' empty'}`}
            onClick={(e) => { e.stopPropagation(); if (hasKids) toggleCollapse(f.uuid) }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
          </span>
          <span className="folder-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>
          </span>
          <span className="folder-name">{f.name}</span>
          {childCount > 0 && <span className="folder-count">{childCount}</span>}
          <span className="row-actions-inline">
            <button type="button" className="folder-actions-btn" title="More" aria-label="Folder actions"
              onClick={(e) => openPopoverFor(e, folderMenu(f))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
            </button>
          </span>
        </li>,
      )
      if (f.children && f.children.length > 0 && !isCollapsed) out.push(...renderTree(f.children, depth + 1, f.uuid))
      if (!isCollapsed) {
        const childDocs = docsByFolder.get(f.id) ?? []
        for (const d of childDocs) {
          out.push(
            <li
              key={d.uuid}
              className={`folder-item doc-tree-item doc-item${selectedDocUuid === d.uuid ? ' active' : ''}${isSelected('document', d.uuid) ? ' multi-selected' : ''}`}
              data-doc-uuid={d.uuid}
              data-parent-folder-uuid={f.uuid}
              style={{ paddingLeft: (depth + 1) * 14 + 22 }}
              draggable
              onDragStart={(e) => onDocDragStart(e, d)}
              onDragEnd={clearDrag}
              onClick={(e) => {
                const t = e.target as HTMLElement
                if (t.closest('.row-actions-inline')) return
                if (t.closest('.uuid-badge')) return
                if (handleSelectionClick(e, { kind: 'document', uuid: d.uuid, name: d.title })) return
                onSelectDoc?.(d)
              }}
              onContextMenu={(e) => openPopoverFor(e, docMenu(d))}
            >
              <span className="folder-name doc-item-title">{d.title}</span>
              <span className="row-actions-inline">
                <button type="button" className="doc-actions-btn" title="More" aria-label="Document actions"
                  onClick={(e) => openPopoverFor(e, docMenu(d))}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
                </button>
              </span>
            </li>,
          )
        }
      }
    }
    return out
  }

  const rootFolderCount = (tree.data?.length ?? 0) + rootDocs.length
  return (
    <>
      <div className="sidebar-inner"
        onDragOver={onRootDragOver}
        onDrop={onDrop}
      >
        <div id="sidebar-header" className="sidebar-header"
             onContextMenu={(e) => openPopoverFor(e, projectMenu())}>
          <button type="button" id="sidebar-project-button" className="sidebar-header-title"
                  onClick={() => onSelectFolder(null)}
                  title={`Open ${project.name}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
            </svg>
            <span id="sidebar-project-name" className="name">{project.name}</span>
            {rootFolderCount > 0 && <span className="folder-count">{rootFolderCount}</span>}
          </button>
          <span className="sidebar-header-actions">
            <button id="btn-new-folder" type="button" className="sidebar-header-btn"
                    title="New folder" aria-label="New folder"
                    onClick={() => setNewChildOf({ parent: null, isRoot: true })}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14"/><path d="M12 5v14"/>
              </svg>
            </button>
            <button id="btn-scroll-from-source" type="button" className="sidebar-header-btn"
                    title="Scroll from current document" aria-label="Scroll from current document"
                    disabled={!selectedDocUuid} onClick={scrollFromSource}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2.5"/>
                <path d="M12 2v3"/><path d="M12 19v3"/><path d="M2 12h3"/><path d="M19 12h3"/>
              </svg>
            </button>
            <button id="btn-sidebar-refresh" type="button" className="sidebar-header-btn"
                    title="Refresh tree" aria-label="Refresh tree"
                    onClick={() => { tree.refetch(); docs.refetch() }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/>
              </svg>
            </button>
            <button id="btn-expand-all" type="button" className="sidebar-header-btn"
                    title="Expand all" aria-label="Expand all" onClick={expandAll}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/>
              </svg>
            </button>
            <button id="btn-collapse-all" type="button" className="sidebar-header-btn"
                    title="Collapse all" aria-label="Collapse all" onClick={collapseAll}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m7 20 5-5 5 5"/><path d="m7 4 5 5 5-5"/>
              </svg>
            </button>
          </span>
        </div>
        {selected.size > 0 && (
          <div id="multi-select-toolbar" className="multi-select-toolbar" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 10px', gap: 8,
            background: 'var(--accent-soft, rgba(58, 113, 255, 0.08))',
            borderBottom: '1px solid var(--border-subtle)',
            fontSize: 12, position: 'sticky', top: 32, zIndex: 5,
          }}>
            <span><strong>{selected.size}</strong> selected</span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button id="btn-multi-move" type="button" className="btn btn-sm btn-primary"
                      onClick={() => { setMoveError(null); setMoveItems(Array.from(selected.values())) }}>
                Move…
              </button>
              <button id="btn-multi-clear" type="button" className="btn btn-sm btn-ghost"
                      onClick={clearSelection}>
                Clear
              </button>
            </span>
          </div>
        )}
        <ul className={`folder-tree loaded${dropTarget?.kind === 'root' ? ' drop-target' : ''}`} id="folder-tree">
          {tree.data && renderTree(tree.data, 0, '')}
          {rootDocs.map((d) => (
            <li
              key={d.uuid}
              className={`folder-item doc-tree-item doc-item${selectedDocUuid === d.uuid ? ' active' : ''}${isSelected('document', d.uuid) ? ' multi-selected' : ''}`}
              data-doc-uuid={d.uuid}
              data-parent-folder-uuid=""
              style={{ paddingLeft: 36 }}
              draggable
              onDragStart={(e) => onDocDragStart(e, d)}
              onDragEnd={clearDrag}
              onClick={(e) => {
                const t = e.target as HTMLElement
                if (t.closest('.row-actions-inline')) return
                if (t.closest('.uuid-badge')) return
                if (handleSelectionClick(e, { kind: 'document', uuid: d.uuid, name: d.title })) return
                onSelectDoc?.(d)
              }}
              onContextMenu={(e) => openPopoverFor(e, docMenu(d))}
            >
              <span className="folder-name doc-item-title">{d.title}</span>
              <span className="row-actions-inline">
                <button type="button" className="doc-actions-btn" title="More" aria-label="Document actions"
                  onClick={(e) => openPopoverFor(e, docMenu(d))}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
                </button>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <Popover items={popover?.items ?? []} anchor={popover?.anchor ?? null} onClose={() => setPopover(null)} />

      <MoveDialog
        open={!!moveItems && moveItems.length > 0}
        items={moveItems ?? []}
        defaultProjectUuid={project.uuid}
        sourceProjectName={project.name}
        pending={moveFolder.isPending || moveDocument.isPending}
        error={moveError}
        onSubmit={async (targetFolderUuid, targetProjectUuid) => {
          if (!moveItems || moveItems.length === 0) return
          setMoveError(null)
          // root sentinel → null. root 이동 시에만 targetProjectUuid 가 의미 있음 (다른 프로젝트의 root).
          const isRoot = targetFolderUuid === MOVE_TARGET_ROOT
          const folderTarget: string | null = isRoot ? null : targetFolderUuid
          const docTarget: string | null = isRoot ? null : targetFolderUuid
          const projectForRoot = isRoot ? targetProjectUuid : null
          try {
            const folders = moveItems.filter((it) => it.kind === 'folder')
            const docs = moveItems.filter((it) => it.kind === 'document')
            const folderResults = await Promise.allSettled(
              folders.map((it) => moveFolder.mutateAsync({
                uuid: it.uuid, parentUuid: folderTarget, targetProjectUuid: projectForRoot,
              })),
            )
            const docResults = await Promise.allSettled(
              docs.map((it) => moveDocument.mutateAsync({
                uuid: it.uuid, folderUuid: docTarget, targetProjectUuid: projectForRoot,
              })),
            )
            const failures = [...folderResults, ...docResults].filter((r) => r.status === 'rejected') as PromiseRejectedResult[]
            if (failures.length > 0) {
              const first = failures[0].reason
              setMoveError(`${failures.length} of ${moveItems.length} failed — ${first?.message ?? first}`)
              return  // dialog 유지 — 사용자가 닫거나 재시도
            }
            setMoveItems(null)
            clearSelection()
          } catch (e) {
            console.error('move failed', e)
            setMoveError(e instanceof Error ? e.message : String(e))
          }
        }}
        onCancel={() => { setMoveItems(null); setMoveError(null) }}
      />

      <PromptDialog open={!!renameFolder} title="Rename Folder" label="Folder name" defaultValue={renameFolder?.name ?? ''}
        id="modal-rename-folder" inputId="folder-name" confirmButtonId="btn-save-folder"
        onSubmit={(name) => {
          if (!name || !renameFolder) { setRenameFolder(null); return }
          updateFolder.mutate({ uuid: renameFolder.uuid, name }, { onSuccess: () => setRenameFolder(null) })
        }}
        onCancel={() => setRenameFolder(null)} />
      <PromptDialog open={!!renameDoc} title="Rename Document" label="Title" defaultValue={renameDoc?.title ?? ''}
        id="modal-rename-doc" inputId="doc-title" confirmButtonId="btn-save-doc"
        onSubmit={(title) => {
          if (!title || !renameDoc) { setRenameDoc(null); return }
          updateDocument.mutate({ uuid: renameDoc.uuid, req: { title, content: renameDoc.content } }, {
            onSuccess: () => setRenameDoc(null),
          })
        }}
        onCancel={() => setRenameDoc(null)} />
      <PromptDialog open={!!newChildOf}
        title={newChildOf?.isRoot ? 'New Folder' : `New folder in '${newChildOf?.parent?.name ?? ''}'`}
        id="modal-new-folder" inputId="folder-name" confirmButtonId="btn-create-folder"
        label="Folder name" confirmText="Create"
        onSubmit={(name) => {
          if (!name || !newChildOf) { setNewChildOf(null); return }
          createFolder.mutate({ name, parentUuid: newChildOf.parent?.uuid ?? null }, {
            onSuccess: () => setNewChildOf(null),
          })
        }}
        onCancel={() => setNewChildOf(null)} />
      <ConfirmDialog open={!!confirmDeleteFolder}
        title={`Delete folder '${confirmDeleteFolder?.name ?? ''}'?`}
        message="The folder and its contents will be archived." confirmText="Delete" danger
        onConfirm={() => { if (confirmDeleteFolder) deleteFolder.mutate(confirmDeleteFolder.uuid, { onSuccess: () => setConfirmDeleteFolder(null) }) }}
        onCancel={() => setConfirmDeleteFolder(null)} />
      <ConfirmDialog open={!!confirmDeleteDoc}
        title={`Delete document '${confirmDeleteDoc?.title ?? ''}'?`}
        message="The document and its versions/attachments will be archived." confirmText="Delete" danger
        onConfirm={() => { if (confirmDeleteDoc) deleteDocument.mutate(confirmDeleteDoc.uuid, { onSuccess: () => setConfirmDeleteDoc(null) }) }}
        onCancel={() => setConfirmDeleteDoc(null)} />
      <PromptDialog open={renameProjectOpen} title="Rename Project" label="Name" defaultValue={project.name}
        id="modal-rename-project" inputId="project-name" confirmButtonId="btn-save-project"
        onSubmit={async (name) => {
          if (!name) { setRenameProjectOpen(false); return }
          await apiPut(`/api/projects/${project.uuid}`, {
            name, description: project.description ?? '', publicReadable: project.publicReadable,
          })
          qc.invalidateQueries({ queryKey: ['projects', 'key', project.projectKey] })
          setRenameProjectOpen(false)
        }}
        onCancel={() => setRenameProjectOpen(false)} />
      <ConfirmDialog open={confirmDeleteProject}
        title={`Delete project '${project.name}'?`}
        message="The project and its folders/documents will be archived." confirmText="Delete" danger
        onConfirm={async () => {
          try { await apiDelete(`/api/projects/${project.uuid}`); navigate('/groups') }
          finally { setConfirmDeleteProject(false) }
        }}
        onCancel={() => setConfirmDeleteProject(false)} />
    </>
  )
}

function key(projectUuid: string) { return `folder-collapsed:${projectUuid}` }
function loadCollapsed(projectUuid: string): Set<string> {
  try {
    const raw = localStorage.getItem(key(projectUuid))
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? new Set<string>(arr) : new Set()
  } catch { return new Set() }
}
function saveCollapsed(projectUuid: string, collapsed: Set<string>): void {
  try { localStorage.setItem(key(projectUuid), JSON.stringify(Array.from(collapsed))) } catch { /* ignore */ }
}
